/**
 * Audio Mixer
 *
 * Mixes multiple audio tracks using Web Audio API
 * Handles volume, fades, and clip trimming
 */

import { Clip } from './types';
import { getClipExportSourceUrl } from './utils/clip-helpers';

interface AudioClipData {
  clip: Clip;
  // FIX: Cannot find name 'AudioBuffer'. Use `any` as a workaround for missing WebCodecs types.
  audioBuffer: any;
}

/**
 * Audio Mixer class
 */
export class AudioMixer {
  // FIX: Cannot find name 'AudioContext'. Use `any` as a workaround for missing WebCodecs types.
  private audioContext: any;
  private sampleRate: number;
  private audioClipData = new Map<string, AudioClipData>();
  // Cache to prevent fetching and decoding the same URL multiple times
  private audioBufferCache = new Map<string, Promise<any>>();

  constructor(sampleRate: number = 48000) {
    this.sampleRate = sampleRate;
    // FIX: Cannot find name 'AudioContext'. Access constructor from `window` as a workaround for missing WebCodecs types.
    this.audioContext = new ((window as any).AudioContext)({ sampleRate });
  }

  /**
   * Preload all audio for clips
   */
  async preloadAudio(clips: Clip[]): Promise<void> {
    const loadPromises: Promise<void>[] = [];

    for (const clip of clips) {
      if ((clip.type === 'video' || clip.type === 'audio') && getClipExportSourceUrl(clip)) {
        loadPromises.push(this.loadAudioClip(clip));
      }
    }

    await Promise.all(loadPromises);
  }

  /**
   * Load audio for a clip
   * Utilise activeAudioSource pour déterminer quelle URL charger (original ou amélioré)
   */
  private async loadAudioClip(clip: Clip): Promise<void> {
    try {
      // Déterminer quelle URL utiliser basée sur activeAudioSource
      let audioUrl = getClipExportSourceUrl(clip);

      if (clip.audioSources && clip.activeAudioSource) {
        const activeSource = clip.audioSources.find(s => s.type === clip.activeAudioSource);
        if (activeSource) {
          audioUrl = activeSource.url;
          console.log(`Using ${clip.activeAudioSource} audio for clip ${clip.name}`);
        }
      }

      if (!audioUrl) {
        console.warn(`No audio URL found for clip ${clip.name}`);
        return;
      }

      // Check cache first
      if (!this.audioBufferCache.has(audioUrl)) {
        // Create a single promise for fetching and decoding this URL
        const decodePromise = (async () => {
          // Add a timeout signal to prevent hanging indefinitely
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 mins timeout

          try {
            const response = await fetch(audioUrl, { signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const arrayBuffer = await response.arrayBuffer();
            clearTimeout(timeoutId);
            return await this.audioContext.decodeAudioData(arrayBuffer);
          } catch (e) {
            clearTimeout(timeoutId);
            throw e;
          }
        })();

        this.audioBufferCache.set(audioUrl, decodePromise);
      }

      const audioBuffer = await this.audioBufferCache.get(audioUrl);

      this.audioClipData.set(clip.id, {
        clip,
        audioBuffer,
      });
    } catch (error) {
      console.error(`Failed to load audio for clip ${clip.name}:`, error);
      // Don't throw - just skip this clip's audio
    }
  }

  /**
   * Mix audio and generate AudioData for encoding
   */
  async *generateAudioFrames(
    clips: Clip[],
    duration: number,
    frameDuration: number = 0.02 // 20ms frames
    // FIX: Cannot find name 'AudioData'. Use `any` as a workaround for missing WebCodecs types.
  ): AsyncGenerator<any> {
    const framesPerSecond = 1 / frameDuration;
    const samplesPerFrame = Math.floor(this.sampleRate * frameDuration);
    const totalFrames = Math.ceil(duration / frameDuration);

    // Create output buffer for mixing
    const leftChannel = new Float32Array(samplesPerFrame);
    const rightChannel = new Float32Array(samplesPerFrame);

    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
      const frameStartTime = frameIndex * frameDuration;
      const frameEndTime = frameStartTime + frameDuration;

      // Clear buffers
      leftChannel.fill(0);
      rightChannel.fill(0);

      // Mix all active clips at this time
      for (const clip of clips) {
        if (frameStartTime >= clip.start && frameStartTime < clip.start + clip.duration) {
          this.mixClipIntoBuffer(
            clip,
            frameStartTime,
            frameDuration,
            leftChannel,
            rightChannel
          );
        }
      }

      // Create AudioData
      // FIX: Cannot find name 'AudioData'. Access constructor from `window` as a workaround for missing WebCodecs types.
      const audioData = new (window as any).AudioData({
        format: 'f32', // Use interleaved format to match interleaved data
        sampleRate: this.sampleRate,
        numberOfFrames: samplesPerFrame,
        numberOfChannels: 2,
        timestamp: frameStartTime * 1000000, // microseconds
        data: this.interleaveChannels(leftChannel, rightChannel),
      });

      yield audioData;
    }
  }

  /**
   * Mix a clip's audio into the output buffers
   */
  private mixClipIntoBuffer(
    clip: Clip,
    frameStartTime: number,
    frameDuration: number,
    leftBuffer: Float32Array,
    rightBuffer: Float32Array
  ): void {
    const clipData = this.audioClipData.get(clip.id);
    if (!clipData) return;

    const { audioBuffer } = clipData;

    // Calculate clip-relative time
    const relativeTime = frameStartTime - clip.start;
    const clipTime = relativeTime + clip.trimStart;

    // Check if we're within the audio duration
    if (clipTime < 0 || clipTime >= audioBuffer.duration) return;

    // Calculate volume with fades
    let volume = clip.volume !== undefined ? clip.volume : 1.0;

    if (relativeTime < clip.fadeInDuration) {
      volume *= relativeTime / clip.fadeInDuration;
    }
    const fadeOutStart = clip.duration - clip.fadeOutDuration;
    if (relativeTime > fadeOutStart) {
      volume *= (clip.duration - relativeTime) / clip.fadeOutDuration;
    }

    if (volume <= 0) return;

    // Get source audio data
    const sourceLeft = audioBuffer.getChannelData(0);
    const sourceRight = audioBuffer.numberOfChannels > 1
      ? audioBuffer.getChannelData(1)
      : sourceLeft;

    // Calculate sample positions
    const startSample = Math.floor(clipTime * audioBuffer.sampleRate);
    const samplesNeeded = Math.floor(frameDuration * this.sampleRate);

    // Mix samples
    for (let i = 0; i < samplesNeeded; i++) {
      const sourceSample = startSample + Math.floor((i / this.sampleRate) * audioBuffer.sampleRate);

      if (sourceSample >= 0 && sourceSample < sourceLeft.length) {
        leftBuffer[i] += sourceLeft[sourceSample] * volume;
        rightBuffer[i] += sourceRight[sourceSample] * volume;

        // Clamp to prevent clipping
        leftBuffer[i] = Math.max(-1, Math.min(1, leftBuffer[i]));
        rightBuffer[i] = Math.max(-1, Math.min(1, rightBuffer[i]));
      }
    }
  }

  /**
   * Interleave stereo channels for AudioData
   */
  private interleaveChannels(left: Float32Array, right: Float32Array): Float32Array {
    const interleaved = new Float32Array(left.length + right.length);
    for (let i = 0; i < left.length; i++) {
      interleaved[i * 2] = left[i];
      interleaved[i * 2 + 1] = right[i];
    }
    return interleaved;
  }

  /**
   * Cleanup
   */
  async cleanup(): Promise<void> {
    this.audioClipData.clear();
    if (this.audioContext.state !== 'closed') {
      await this.audioContext.close();
    }
  }
}
